import { isHomepage } from '@lib/utils/page-rules/by-slug'

import { HOMEPAGE_HERO_IMAGE_COLUMNS } from '@constants/globals'

import { updateAssetCDNUrl } from '@components/asset/utils'
import { getPictureAttributes } from '@components/contentful-content-types/asset-image/utils'

import { getRandomHeroPerson } from '@custom/shared/const'

interface HeroImagePreloadProps {
  rel: string
  as: string
  href: string
  imageSrcSet?: string
  imageSizes: string
  fetchPriority: 'high' | 'low' | 'auto'
}

interface HeroImagePreload {
  key: string
  props: HeroImagePreloadProps
}

// Generate a unique seed for hero image selection on homepage
export const getHomepageHeroSeed = (slug: string = '') =>
  isHomepage(slug ?? '') ? Math.abs(Math.floor(Math.random() * 1_000_000)) : null

/**
 * Generates preload link properties for the hero image that match exactly
 * what the Hero component's Picture element will request. This ensures
 * optimal cache hits and LCP performance.
 */
export function getHeroImagePreloadProps(heroImageSeed: number): HeroImagePreload | null {
  const selectedImage = getRandomHeroPerson(heroImageSeed)

  // Use the EXACT same logic as the Hero component's Picture element
  const pictureAttributes = getPictureAttributes({
    width: selectedImage.width ?? 0,
    height: selectedImage.height ?? 0,
    url: updateAssetCDNUrl(selectedImage.path),
    isSvg: false,
    cols: HOMEPAGE_HERO_IMAGE_COLUMNS,
  })

  const { src, srcSets, sizes } = pictureAttributes

  // Use the same fallback src that Picture component uses
  if (!src) return null

  // Combine all formats into a comprehensive srcset for browser choice (if available)
  const allSrcSets = srcSets
    ? [...srcSets.avif, ...srcSets.webp, ...srcSets.original].join(', ')
    : undefined

  return {
    key: 'hero-person-preload',
    props: {
      rel: 'preload',
      as: 'image',
      href: src,
      imageSrcSet: allSrcSets,
      imageSizes: sizes.join(', '),
      fetchPriority: 'high',
    },
  }
}
